import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";
import { toUid } from "@/helpers/utility/Id";
import { groupBy } from "lodash-es";

import { useConditionStore } from "@editor/items/condition/conditions.store";

import type { RequirementItem } from "./RequirementItem";

/**
 * Requirements are groups of conditions that must be fulfilled before activating an action.
 * All conditions must apply before activating the action.
 */

const itemStructure: RequirementItem = {
  type: "requirement",
  id: -1,
  uid: "requirement_0000",
  createdAt: -1,

  itemId: -1,
  itemType: "",

  title: "",
  description: ""
};

export const useRequirementStore = defineItemStore(
  "model/requirements",
  itemStructure,
  {
    getters: {
      byItemUid(state) {
        return groupBy(state.items, (item) => toUid(item.itemType, item.itemId));
      }
    },

    events: {
      async afterDelete(requirement) {
        // delete all conditions that are connected with this requirement
        const conditionStore = useConditionStore();
        const conditions = conditionStore.getBySource(requirement);
        if (conditions.length > 0) {
          await conditionStore.deleteMany(conditions);
        }

        return requirement;
      }
    },

    actions: {
      async duplicate(id: number, changes: Partial<RequirementItem> = {}) {
        const newRequirement = await this.super.duplicate(id, changes);
        const conditionStore = useConditionStore();
        const conditions = conditionStore.byRequirement[id] ?? [];
        for (const condition of conditions) {
          await conditionStore.duplicate(condition.id, {
            srcType: "requirement",
            srcId: newRequirement.id
          });
        }
        return newRequirement;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useRequirementStore, import.meta.hot)
  );
}
